import { useState } from 'react';
import axios from 'axios';
import { Loader, FileText, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_TYPES = ['V', 'E', 'J', 'G', 'P'];

export default function DomiciliacionPayment({ order, onSuccess, onError }) {
  const dom = order?.domiciliacion || {};
  const [docType, setDocType] = useState('V');
  const [docNumber, setDocNumber] = useState('');
  const [cuenta, setCuenta] = useState('');
  const [nombre, setNombre] = useState(order?.payer_name || '');
  const [processing, setProcessing] = useState(false);
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setPending(false);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/public/orders/${order.order_id}/domiciliacion/charge`,
        { identificacion: `${docType}${docNumber}`, cuenta: cuenta.trim(), nombre: nombre.trim() },
      );
      if (res.data.success) {
        onSuccess?.();
      } else if (res.data.pending) {
        setPending(true);
      } else {
        onError?.(res.data.mensaje || 'Operación rechazada por el banco');
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      onError?.(typeof d === 'string' ? d : (d?.error || err.message));
    } finally {
      setProcessing(false);
    }
  };

  if (!dom.contrato) {
    return (
      <div className="text-center py-6 text-gray-500" data-testid="domiciliacion-no-contract">
        Este comercio no configuró los datos de domiciliación para esta orden.
      </div>
    );
  }

  if (pending) {
    return (
      <div className="text-center py-8" data-testid="domiciliacion-pending">
        <Clock className="w-12 h-12 mx-auto text-amber-500 mb-3" />
        <p className="font-semibold text-gray-800">Cobro pendiente por liquidación (BCV)</p>
        <p className="text-sm text-gray-600 mt-1">Se confirmará automáticamente en unos minutos. Puedes cerrar esta ventana.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="domiciliacion-form">
      <div className="border-2 border-blue-100 rounded-xl p-4" style={{ backgroundColor: '#E8F3FA' }}>
        <p className="text-sm font-medium" style={{ color: '#003594' }}>
          Se cobrará contra tu contrato de domiciliación autorizado. Confirma los datos de tu cuenta.
        </p>
      </div>

      <div className="bg-gray-50 border rounded-xl p-3 text-sm" data-testid="domiciliacion-contract-info">
        <div className="flex items-center gap-2 text-gray-500 text-xs font-semibold uppercase tracking-wide mb-1">
          <FileText className="w-3.5 h-3.5" /> Contrato de domiciliación
        </div>
        <p className="font-mono text-gray-700">N° {dom.contrato} · {dom.fecha_contrato}</p>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Cédula / RIF del titular *</label>
        <div className="grid grid-cols-4 gap-2">
          <select
            value={docType}
            onChange={(e) => setDocType(e.target.value)}
            className="px-3 py-3 border-2 border-gray-300 rounded-xl text-center font-semibold"
            style={{ fontSize: '15px' }}
            data-testid="domiciliacion-doctype"
          >
            {DOC_TYPES.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <input
            type="text"
            value={docNumber}
            onChange={(e) => setDocNumber(e.target.value.replace(/[^0-9]/g, ''))}
            className="col-span-3 px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
            style={{ fontSize: '15px' }}
            placeholder="12345678"
            maxLength={11}
            required
            data-testid="domiciliacion-docnumber"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Cuenta a debitar (20 dígitos) *</label>
        <input
          type="text"
          value={cuenta}
          onChange={(e) => setCuenta(e.target.value.replace(/[^0-9]/g, ''))}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500 font-mono"
          style={{ fontSize: '15px' }}
          placeholder="01380007350070010802"
          maxLength={20}
          required
          data-testid="domiciliacion-cuenta"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre del titular</label>
        <input
          type="text"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:border-blue-500"
          style={{ fontSize: '15px' }}
          placeholder="Nombre completo"
          maxLength={80}
          data-testid="domiciliacion-nombre"
        />
      </div>

      <button
        type="submit"
        disabled={processing}
        className="w-full py-4 px-4 text-white rounded-xl font-bold text-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl"
        style={{ backgroundColor: '#003594' }}
        data-testid="domiciliacion-submit-btn"
      >
        {processing ? (<><Loader className="w-6 h-6 animate-spin" /> Procesando cobro...</>) : `Pagar ${Number(order.amount).toLocaleString('es-VE')} ${order.currency}`}
      </button>
    </form>
  );
}
